'use client';

import { varAlpha } from 'minimal-shared/utils';

import { styled } from '@mui/material/styles';

import { RADIUS } from 'src/theme/ui-tokens';

// ----------------------------------------------------------------------

type LabelRootProps = { color?: string; variant?: string; disabled?: boolean };

export const LabelRoot = styled('span', {
  shouldForwardProp: (prop: string) => !['color', 'variant', 'disabled', 'sx'].includes(prop),
})<LabelRootProps>(({ color, variant, disabled, theme }) => {
  // ⚠️ `color` 는 팔레트 키 문자열로 온다 — 색인 접근만 넓히고 값·분기는 그대로 둔다
  const palette = theme.vars.palette as any;
  const defaultStyles = {
    ...(color === 'default' && {
      /**
       * @variant filled
       */
      ...(variant === 'filled' && {
        color: theme.vars.palette.common.white,
        backgroundColor: theme.vars.palette.text.primary,
        ...theme.applyStyles('dark', {
          color: theme.vars.palette.grey[800],
        }),
      }),
      /**
       * @variant outlined
       */
      ...(variant === 'outlined' && {
        backgroundColor: 'transparent',
        color: theme.vars.palette.text.primary,
        border: `2px solid ${theme.vars.palette.text.primary}`,
      }),
      /**
       * @variant soft
       */
      ...(variant === 'soft' && {
        color: theme.vars.palette.text.secondary,
        backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.16),
      }),
      /**
       * @variant inverted
       */
      ...(variant === 'inverted' && {
        color: theme.vars.palette.grey[800],
        backgroundColor: theme.vars.palette.grey[300],
      }),
    }),
  };

  const colorStyles = {
    ...(color &&
      color !== 'default' && {
        /**
         * @variant filled
         */
        ...(variant === 'filled' && {
          color: palette[color as string]?.contrastText || theme.vars.palette.common.white,
          backgroundColor: palette[color as string]?.main || theme.vars.palette.text.primary,
        }),
        /**
         * @variant outlined
         */
        ...(variant === 'outlined' && {
          backgroundColor: 'transparent',
          color: palette[color as string]?.main || theme.vars.palette.text.primary,
          border: `2px solid ${palette[color as string]?.main || theme.vars.palette.text.primary}`,
        }),
        /**
         * @variant soft
         */
        ...(variant === 'soft' && {
          color: palette[color as string]?.dark || theme.vars.palette.text.primary,
          backgroundColor: varAlpha((palette[color as string]?.mainChannel ?? theme.vars.palette.grey['500Channel']), 0.16),
          ...theme.applyStyles('dark', {
            color: palette[color as string]?.light || theme.vars.palette.common.white,
          }),
        }),
        /**
         * @variant inverted
         */
        ...(variant === 'inverted' && {
          color: palette[color as string]?.darker || theme.vars.palette.text.primary,
          backgroundColor: palette[color as string]?.lighter || theme.vars.palette.grey[300],
        }),
      }),
  };

  return {
    height: 24,
    minWidth: 24,
    lineHeight: 0,
    flexShrink: 0,
    cursor: 'default',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    gap: theme.spacing(0.75),
    justifyContent: 'center',
    padding: theme.spacing(0, 0.75),
    fontSize: theme.typography.pxToRem(12),
    /** ⚠️ 표딱지는 **600** — 700 은 화면 제목 하나뿐이다(디자인 시스템 1장 「굵기 규칙」) */
    fontWeight: theme.typography.fontWeightSemiBold,
    /** ⚠️ 모서리는 사다리에서 고른다 — 표딱지는 가장 작은 칸(`RADIUS.sm`)이다 */
    borderRadius: RADIUS.sm,
    transition: theme.transitions.create(['all'], { duration: theme.transitions.duration.shorter }),
    ...defaultStyles,
    ...colorStyles,
    ...(disabled && { opacity: 0.48, pointerEvents: 'none' }),
  };
});

// ----------------------------------------------------------------------

export const LabelIcon = styled('span')({
  width: 16,
  height: 16,
  flexShrink: 0,
  '& svg, img': { width: '100%', height: '100%', objectFit: 'cover' },
});
